import { ArrowIcon, TabButton } from 'components';
import { EnergyDisplayMode, EnergyUseChart } from 'components/EnergyUseChart';
import { MainLayoutContext } from 'layouts/MainLayout';
import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { selectEnergyUsage } from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  alignItems,
  borderColor,
  borderStyle,
  borderWidth,
  display,
  flexDirection,
  fontSize,
  gap,
  justifyContent,
  letterSpacing,
  lineHeight,
  margin,
  overflow,
  padding,
  textAlign,
  textColor,
  twCls,
} from 'style';

type InfoTileProps = {
  header: string;
  value: ReactNode;
  unit: string;
};

const InfoTile: FC<InfoTileProps> = ({ header, value, unit }) => (
  <div className={twCls(display('flex'), flexDirection('flex-col'))}>
    <h3 className={twCls(fontSize('text-sm'), textAlign('text-center'))}>{header}</h3>
    <div
      className={twCls(
        display('flex'),
        flexDirection('flex-col'),
        textColor('text-blue-100'),
        margin('mt-3'),
      )}
    >
      <div
        className={twCls(
          textAlign('text-center'),
          fontSize('text-3xl'),
          letterSpacing('tracking-tighter'),
        )}
      >
        {value}
      </div>
      <div
        className={twCls(
          textAlign('text-center'),
          fontSize('text-xs'),
          letterSpacing('tracking-tighter'),
        )}
      >
        {unit}
      </div>
    </div>
  </div>
);

type UsageTileProps = {
  value: number;
};

const UsageTile: FC<UsageTileProps> = ({ value }) => (
  <InfoTile
    header="Usage"
    value={
      <div className={twCls(display('flex'), gap('gap-1'), alignItems('items-end'))}>
        <div>{Math.abs(value * 100).toFixed(1)}</div>
        <div className={twCls(fontSize('text-xs'), lineHeight('leading-6'))}>%</div>
        <ArrowIcon className={twCls(margin('mb-2'))} direction={value > 0 ? 'up' : 'down'} />
      </div>
    }
    unit={value > 0 ? 'UP' : 'DOWN'}
  />
);

type EnergyViewState = 'weekly' | 'monthly' | 'yearly';

export const Energy = (): JSX.Element => {
  const [state, setState] = useState<EnergyViewState>('weekly');
  const { showNavigationButtons, showUserProfile } = useContext(MainLayoutContext);
  const energyUse = useAppSelector((state) => selectEnergyUsage(state));

  useEffect(() => {
    showNavigationButtons(true);
    showUserProfile(true);
  }, [showNavigationButtons, showUserProfile]);

  const energyData = (() => {
    switch (state) {
      case 'weekly':
        return energyUse?.weekly;
      case 'monthly':
        return energyUse?.monthly;
      case 'yearly':
        return energyUse?.yearly;
    }
  })();

  return (
    <div className={twCls(margin('mt-6'), display('flex'), flexDirection('flex-col'))}>
      <h2 className={twCls(display('flex'), justifyContent('justify-center'))}>Energy Use Today</h2>
      {energyUse && (
        <div
          className={twCls(display('flex'), flexDirection('flex-col'), overflow('overflow-auto'))}
        >
          <div
            className={twCls(
              display('flex'),
              justifyContent('justify-center'),
              margin('mt-6'),
              padding('pb-7'),
              borderWidth('border-b'),
              borderStyle('border-solid'),
              borderColor('border-b-grey-200'),
            )}
          >
            <span className={twCls(fontSize('text-5xl'))}>
              {(energyUse.energyUseToday / 1000).toFixed(2)}
            </span>
            <span className={twCls(display('flex'), alignItems('items-end'), padding('pb-0.5'))}>
              kWh
            </span>
          </div>
          <div
            className={twCls(
              margin('mt-4'),
              display('flex'),
              justifyContent('justify-center'),
              gap('gap-3'),
            )}
          >
            <TabButton active={state === 'weekly'} onClick={() => setState('weekly')}>
              Weekly
            </TabButton>
            <TabButton active={state === 'monthly'} onClick={() => setState('monthly')}>
              Monthly
            </TabButton>
            <TabButton active={state === 'yearly'} onClick={() => setState('yearly')}>
              Annual
            </TabButton>
          </div>
          <div
            className={twCls(
              margin('mt-8'),
              display('flex'),
              justifyContent('justify-center'),
              gap('gap-7'),
            )}
          >
            <InfoTile
              header={
                state === 'weekly'
                  ? 'Daily Average'
                  : state === 'monthly'
                  ? 'Weekly Average'
                  : 'Monthly Average'
              }
              value={((energyData?.average ?? 0) / 1000).toFixed(1)}
              unit="kWh"
            />
            <InfoTile
              header="Total Energy"
              value={((energyData?.total ?? 0) / 1000).toFixed(1)}
              unit="kWh"
            />
            <UsageTile value={energyData?.usage ?? 0} />
          </div>
          <div className={twCls(margin('mt-6'))}>
            {state === 'weekly' && (
              <EnergyUseChart
                title="Weekly Use"
                type={EnergyDisplayMode.Weekly}
                data={energyData?.data || []}
                average={energyData?.average || 0}
              />
            )}
            {state === 'monthly' && (
              <EnergyUseChart
                title="Monthly Use"
                type={EnergyDisplayMode.Monthly}
                data={energyData?.data || []}
                average={energyData?.average || 0}
              />
            )}
            {state === 'yearly' && (
              <EnergyUseChart
                title="Yearly Use"
                type={EnergyDisplayMode.Yearly}
                data={energyData?.data || []}
                average={energyData?.average || 0}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
